/**
 * Central object used for Store Auth module
 */

import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import _ from 'lodash'
import Authentication from '@/api/MyLogin/Authentication'
import dayjs, { setDayjsLocale } from '@/plugins/dayjs'
import i18n from '@/plugins/i18n'
import { useAppStore } from '@/stores/app'
import { useUserProfilesStore } from '@/stores/userProfiles'
import { useStudentTasksStore } from '@/stores/studentTasks'
import type IJwt from '@/models/Authentication/Jwt'
import type IOauthTokens from '@/models/Authentication/OauthTokens'
import type IUser from '@/models/User/User'
import type IUserProfile from '@/models/UserProfile/UserProfile'
import { UserProfilePermissionLevel } from '@/enums/UserProfilePermissionLevel'
import { UserProfileType } from '@/models/UserProfile/UserProfileType'

interface AuthState {
  access_token_timeout: ReturnType<typeof setTimeout> | undefined
  user: {
    id: number | null
    has_trial_student: boolean
    name: string | null
    menu_name: string | null
    username: string | null
    timezone: string
  }
  authenticated: boolean
  authorised: boolean
  is_shadowing: boolean
  is_interviewer: boolean
  user_type: string | null
  selected_interviewing: boolean
  selected_organisation_id: number | null
  user_profile: null | IUserProfile
  user_profiles: [] | IUserProfile[]
  shouldClearQueryCache: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    access_token_timeout: undefined,
    user: {
      id: null,
      has_trial_student: false,
      name: null,
      menu_name: null,
      username: null,
      timezone: '',
    },
    authenticated: false,
    authorised: false,
    is_shadowing: false,
    is_interviewer: false, // set via JWT claims before getting loggedInUser
    user_type: null, // set via JWT claims before getting loggedInUser
    selected_interviewing: false,
    selected_organisation_id: null, // set via JWT claims before getting loggedInUser
    user_profile: null,
    user_profiles: [],
    shouldClearQueryCache: false,
  }),

  getters: {
    // Use a getters to access the Stores
    appStore() {
      return useAppStore()
    },

    studentTasksStore() {
      return useStudentTasksStore()
    },

    userProfilesStore() {
      return useUserProfilesStore()
    },

    displayUserProfileSelector: state => {
      if (!state.user_profiles) return false

      return state.user_profiles.length > 1
        || (state.user_profiles.length === 1 && state.selected_interviewing === true)
    },

    hasRefreshToken() {
      const token = Cookies.get('x-refresh-token')

      return (!!token)
    },

    hasValidAccessToken() {
      const token = localStorage.getItem('token')

      if (!token) return false

      try {
        const jwt: IJwt = jwtDecode(token)
        const now = Math.floor(Date.now() / 1000)

        // Check if token is expired (with 30 second buffer)
        return jwt.exp > (now + 30)
      }
      catch {
        return false
      }
    },

    userProfile: state => {
      if (state.user_profile)
        return state.user_profile
    },

    userTimeZone: state => {
      if (state.user.timezone) {
        return state.user.timezone
      }

      return 'Europe/London'
    },

    organisation: state => {
      if (state.user_profile)
        return state.user_profile.organisation
    },

    organisationIsBusinessPartnerClient: state => {
      if (state.user_profile) {
        return !state.user_profile.organisation.requires_customer_agreement
      }
    },

    organisationRegionVariationType: state => {
      if (state.user_profile) {
        return state.user_profile.organisation.programme_region.variation_type
      }
    },

    organisationLogo: state => {
      if (state.user_profile)
        return state.user_profile.organisation.logo
    },

    organisationTimeZone: state => {
      if (state.user_profile) {
        return state.user_profile.organisation.timezone
      }

      return 'Europe/London'
    },

    organisationHasDefaultPassword: state => {
      if (state.user_profile)
        return state.user_profile.organisation.has_default_password

      return false
    },

    currentAcademicYear: state => {
      return state.user_profile?.organisation?.current_academic_year
    },

    previousAcademicYear: state => {
      return state.user_profile?.organisation?.previous_academic_year
    },



    organisationYearSubGroupKnownAs: state => {
      return state.user_profile?.organisation?.year_sub_group_known_as || i18n.global.t('fields.sub_group')
    },

    isBPClient: state => {
      return !!state.user_profile?.organisation?.business_partner_id
    },

    userProfilePermissionLevel: state => {
      if (state.user_profile?.type === UserProfileType.business_partner) {
        return UserProfilePermissionLevel.ADMIN
      }

      return state.user_profile?.permission_level ?? UserProfilePermissionLevel.STUDENT_VIEWER
    },

    canBuyLicences: state => {
      return state.user_profile?.organisation?.has_customer_agreement
        && state.user_profile?.organisation?.requires_customer_agreement
        && state.user_profile?.permission_level === UserProfilePermissionLevel.ADMIN
    },

    unreadNotificationNumber: state => {
      return state.user_profile?.unread_notification_number ?? 0
    },
  },

  actions: {
    completeLogin(data: IUser) {
      const hasUserProfile = (data.user_profiles && data.user_profiles.length > 0)
      const authorised = hasUserProfile || this.is_interviewer
      let selectedUserProfile: IUserProfile | null = null

      // Add user details to store
      this.user = {
        id: data.id,
        has_trial_student: data.has_trial_student,
        name: data.full_name,
        menu_name: null,
        username: data.username,
        timezone: data.timezone,
      }
      this.authenticated = true
      this.authorised = authorised

      dayjs.tz.setDefault(data.timezone)

      const locales = ['en' , 'bg' , 'cy' , 'de' , 'de_CH' , 'es' , 'et', 'fr' , 'ga' , 'gu' , 'hi' , 'hu' , 'lt' , 'lv' , 'nl' , 'pl' , 'ro' , 'sk' , 'tr' , 'uk' , 'zh_CN' , 'zh_TW'] as const
      type Locale = typeof locales[number]
      const locale: Locale = locales.includes(data.locale as Locale) ? data.locale as Locale : 'en'

      this.appStore.locale = locale
      document.documentElement.lang = locale

      if (locale !== localStorage.getItem('locale')) {
        this.shouldClearQueryCache = true
      }

      localStorage.setItem('locale', locale)
      i18n.global.locale.value = locale
      setDayjsLocale(locale)

      if (hasUserProfile)
        this.user_profiles = data.user_profiles

      const selectedInterviewing = localStorage.getItem('selected_interviewing')

      if (selectedInterviewing && selectedInterviewing === 'true') {
        this.setSelectedInterviewing(true)

        if (this.selected_interviewing)
          return
      }

      // get userProfile based on selectedOrganisationId
      selectedUserProfile = _.find(
        this.user_profiles,
        (userProfile: { organisation_id: number }) => {
          return userProfile.organisation_id === this.selected_organisation_id
        }) ?? null

      if (selectedUserProfile) {
        this.setLoggedInUserProfile(selectedUserProfile)
        this.studentTasksStore.setAcademicYearFilter(selectedUserProfile.organisation.current_academic_year.id)
        this.user.menu_name = selectedUserProfile.organisation.name
        dayjs.tz.setDefault(selectedUserProfile.organisation.timezone)
      }

      // Check whether we should display the academic year welcome modal
      if (
        selectedUserProfile?.organisation.current_academic_year.has_been_acknowledged === false
        && this.user_type !== 'admin'
        && selectedUserProfile?.type === UserProfileType.organisation
        && selectedUserProfile?.permission_level !== UserProfilePermissionLevel.STUDENT_VIEWER
      ) {
        this.appStore.academicYearWelcomeModal.shouldDisplay = true
      }
    },

    getLoggedInUser() {
      if (this.selected_organisation_id && this.user_type === 'admin') {
        this.is_shadowing = true

        // Get the logged-in user
        return Authentication.getLoggedInMyHQUserShadow(this.selected_organisation_id)
          .then((data: { user: IUser; user_profile: IUserProfile }) => {
            const loginData: IUser = data.user

            loginData.user_profiles = [data.user_profile]
            this.completeLogin(loginData)
          })
          .catch(error => {
            const isShadowOrgMismatch = error?.message === 'SHADOW_ORG_MISMATCH'

            const message = isShadowOrgMismatch
              ? 'Switching to new organisation'
              : (error?.response?.data?.message
                ?? error?.message
                ?? 'You are not authorised to access this organisation\'s data')

            this.appStore.setSnackbar({
              text: message,
              colour: isShadowOrgMismatch ? 'warning' : 'error',
            })
            // Re-throw so the promise chain knows it failed and prevents navigation
            throw error
          })
      }
      else {
        // Get the logged-in user
        return Authentication.getLoggedInMyHQUser()
          .then(({ data }) => {
            this.completeLogin(data)
          })
      }
    },

    restoreJwtClaims() {
      // Restore JWT claims from the access token
      // This is useful when store state is reset but token still exists (e.g., page refresh)
      const token = localStorage.getItem('token')

      if (!token) return

      try {
        const jwt: IJwt = jwtDecode(token)
        const now = Math.floor(Date.now() / 1000)

        // Only restore if token is valid
        if (jwt.exp > (now + 30)) {
          this.restoreJwtClaimsFromPayload(jwt)
        }
      }
      catch {
        // JWT decode failed - token may be invalid/corrupted, silently fail, likely needs a full page refresh
      }
    },

    restoreJwtClaimsFromToken() {
      // Restore user_type and selected_organisation_id from token even when expired.
      // Used on first load when token is expired - we need these to call the correct endpoint
      // (myhq-user vs myhq-user-shadow). The first request will 401, trigger refresh, then succeed.
      const token = localStorage.getItem('token')

      if (!token) return

      try {
        const jwt: IJwt = jwtDecode(token)

        this.restoreJwtClaimsFromPayload(jwt)
      }
      catch {
        // JWT decode failed - token may be invalid/corrupted
      }
    },

    restoreJwtClaimsFromPayload(jwt: IJwt) {
      if (jwt['user-type'] && !this.user_type) {
        this.user_type = jwt['user-type']
      }
      if (jwt.selected_organisation_id !== undefined && this.selected_organisation_id === null) {
        this.selected_organisation_id = jwt.selected_organisation_id
      }
      if (jwt.is_interviewer !== undefined) {
        this.is_interviewer = jwt.is_interviewer
      }
    },

    processTokens(data: IOauthTokens) {
      const { access_token, refresh_token } = data

      if (!access_token || !refresh_token) {
        this.appStore.setSnackbar({
          text: i18n.global.t('pages.auth.error_authenticating'),
          colour: 'error',
        })

        throw new Error('Invalid tokens received')
      }
      // Decode the Passport JWT
      const jwt: IJwt = jwtDecode(access_token || '')

      // Check claims for a UserType and set it in store
      // We can then check it before determining which login function to use
      if (jwt['user-type']) {
        this.user_type = jwt['user-type']
      }

      // Check claims for a "selected organisation id" and set it in Vuex
      // We can then check it before determining which login function to use
      if (jwt.selected_organisation_id)
        this.selected_organisation_id = jwt.selected_organisation_id

      if (jwt.is_interviewer)
        this.is_interviewer = jwt.is_interviewer

      // Save the refresh token in a cookie
      Cookies.set('x-refresh-token', refresh_token || '')

      // Add the access token in local storage
      localStorage.setItem('token', access_token || '')

      // Clear any existing timeout before setting a new one to prevent multiple timers
      if (this.access_token_timeout) {
        clearTimeout(this.access_token_timeout)
      }

      // Restart the 8-minute timer for next access token refresh 480000
      this.access_token_timeout = setTimeout(() => {
        this.refreshToken()
      }, 480000)
    },

    setLoggedInUserProfile(userProfile: IUserProfile) {
      localStorage.setItem('selected_user_profile', userProfile.id.toString())
      this.user_profile = userProfile
    },

    setSelectedInterviewing(newValue: boolean) {
      if (newValue && this.is_interviewer) {
        localStorage.setItem('selected_interviewing', 'true')
        this.selected_interviewing = true
        this.user_profile = null
        this.user.menu_name = null
      } else {
        localStorage.removeItem('selected_interviewing')
        this.selected_interviewing = false
      }
    },

    setNotificationRead() {
      if (this.user_profile) {
        this.user_profile.unread_notification_number--
      }
    },

    logout() {
      this.removeRefreshToken()

      // Remove access token in local storage
      window.localStorage.removeItem('token')

      // Stop the auto access_token refresh
      clearTimeout(this.access_token_timeout)

      // Clear Selected UserProfile
      localStorage.removeItem('selected_user_profile')
      localStorage.removeItem('selected_interviewing')

      // Reset Auth data in Vuex
      this.resetAuth()
    },

    async refreshToken() {
      const token = Cookies.get('x-refresh-token')

      if (!token) {
        // no refresh token - throw error so axios interceptor can handle redirect
        throw new Error('No refresh token available')
      }

      const params = {
        client_id: this.appStore.config.AUTH_PUBLIC_CLIENT_ID || '',
        grant_type: 'refresh_token',
        refresh_token: token,
      }

      // Attempt to get an access_token from refresh token silently
      // This should only be called by the axios interceptor on 401 errors
      try {
        const data = await Authentication.getToken(params)

        // Only remove old refresh token after successful refresh
        this.removeRefreshToken()
        this.processTokens(data)
      }
      catch (error) {
        // If refresh fails, remove the invalid refresh token
        // But don't redirect here - let the calling code handle it
        this.removeRefreshToken()
        throw error
      }
    },

    removeRefreshToken() {
      Cookies.remove('x-refresh-token')
    },

    resetAuth() {
      this.user = {
        id: null,
        has_trial_student: false,
        name: null,
        menu_name: null,
        username: null,
        timezone: '',
      }
      this.authenticated = false
      this.authorised = false
      this.user_type = null
    },
  },
})
